import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Slider, Text } from '@fragments';
import { IconArrowsExchange, IconMinus, IconPlus, IconRotateClockwise2 } from '@tabler/icons-react';
import SolidPicker from './SolidPicker';
import { fillToCss, mix, stopColor } from '../../../model/colors';
import { LIMITS } from '../../../model/schema';

const MAX_STOPS = LIMITS.gradientStops[1];
const sorted = stops => stops.map((stop, index) => ({ ...stop, index })).sort((a, b) => a.offset - b.offset);
const round = value => Math.round(value * 1000) / 1000;

// The color (and opacity) a gradient has at `offset`, for a stop added there.
function colorAt(stops, offset) {
  const list = sorted(stops);
  const after = list.find(stop => stop.offset >= offset) || list[list.length - 1];
  const before = [...list].reverse().find(stop => stop.offset <= offset) || list[0];
  const span = after.offset - before.offset;
  const t = span > 0 ? (offset - before.offset) / span : 0;
  return { color: mix(before.color, after.color, t), opacity: round(before.opacity + (after.opacity - before.opacity) * t) };
}

// A number field (0–100) for a stop's position or opacity.
function Percent({ value, onCommit, label }) {
  const [text, setText] = useState(null);
  const shown = text === null ? String(Math.round(value * 100)) : text;
  const commit = () => {
    if (text === null) return;
    const number = Number(text.replace(/[^\d.]/g, ''));
    setText(null);
    if (Number.isFinite(number)) onCommit(Math.min(100, Math.max(0, number)) / 100);
  };
  return <label className="art-fill__field" style={{ '--field-width': '30px' }}>
    <input value={shown} aria-label={label} onChange={event => setText(event.target.value)} onBlur={commit} onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
    <span aria-hidden="true">%</span>
  </label>;
}

// Linear or radial gradients like Figma's: the bar shows the stops (drag one to move it,
// press the bar to add one there), the list edits each stop's position, color and opacity
// (the selected stop's color in the picker under the list); flip reverses the stops, rotate turns a linear
// gradient by 90°, and the angle slider turns it freely.
export default function GradientEditor({ fill, onChange, brand, used }) {
  const { t } = useTranslation('artEditor');
  const [active, setActive] = useState(0);
  const bar = useRef(null);
  const stops = fill.stops;
  const current = Math.min(active, stops.length - 1);
  const setStops = next => onChange({ ...fill, stops: next });
  const setStop = (index, patch) => setStops(stops.map((stop, position) => (position === index ? { ...stop, ...patch } : stop)));
  const add = offset => {
    if (stops.length >= MAX_STOPS) return;
    setStops([...stops, { offset: round(offset), ...colorAt(stops, offset) }]);
    setActive(stops.length);
  };
  const addInGap = () => {
    const list = sorted(stops);
    let best = { at: 0.5, size: -1 };
    list.slice(1).forEach((stop, index) => { const size = stop.offset - list[index].offset; if (size > best.size) best = { at: list[index].offset + size / 2, size }; });
    add(best.at);
  };
  const remove = index => { if (stops.length <= 2) return; setStops(stops.filter((_, position) => position !== index)); setActive(0); };
  const offsetAt = clientX => { const rect = bar.current.getBoundingClientRect(); return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)); };
  const dragStop = (index, event) => {
    event.stopPropagation();
    if (event.button !== 0) return;
    setActive(index);
    const element = event.currentTarget;
    element.setPointerCapture?.(event.pointerId);
    const move = next => setStop(index, { offset: round(offsetAt(next.clientX)) });
    const stop = () => { element.removeEventListener('pointermove', move); element.removeEventListener('pointerup', stop); element.removeEventListener('pointercancel', stop); };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', stop);
    element.addEventListener('pointercancel', stop);
  };
  const preview = { type: 'linear', angle: 90, stops };
  const types = [{ value: 'linear', label: t('gradientLinear') }, { value: 'radial', label: t('gradientRadial') }];
  const switchType = type => onChange(type === 'radial' ? { type: 'radial', stops } : { type: 'linear', angle: fill.angle ?? 180, stops });
  return <div className="art-fill__gradient">
    <div className="art-fill__row">
      <Menu placement="bottom-start" items={types.map(item => ({ label: item.label, active: item.value === fill.type, onClick: () => switchType(item.value) }))}>
        <Button size="small" variant="text" color="overlay" dropdown preventLoading aria-label={t('gradientType')}>{types.find(item => item.value === fill.type)?.label}</Button>
      </Menu>
      <span className="art-fill__spacer" />
      <Button size="small" variant="text" color="overlay" preventLoading icon={<IconArrowsExchange />} aria-label={t('gradientFlip')} tooltip={t('gradientFlip')}
        onClick={() => setStops(stops.map(stop => ({ ...stop, offset: round(1 - stop.offset) })))} />
      {fill.type === 'linear' && <Button size="small" variant="text" color="overlay" preventLoading icon={<IconRotateClockwise2 />} aria-label={t('gradientRotate')} tooltip={t('gradientRotate')}
        onClick={() => onChange({ ...fill, angle: (Math.round(fill.angle / 90) * 90 + 90) % 360 })} />}
    </div>
    <div ref={bar} className="art-fill__bar" onPointerDown={event => { if (event.target === bar.current) add(offsetAt(event.clientX)); }} role="group" aria-label={t('gradientStops')}>
      <span className="art-fill__bar-paint" style={{ background: fillToCss(preview) }} aria-hidden="true" />
      {stops.map((stop, index) => <button key={index} type="button" className={`art-fill__handle ${index === current ? 'is-active' : ''}`} style={{ left: `${stop.offset * 100}%`, '--swatch': stopColor(stop) }}
        aria-label={`${t('gradientStop')} ${Math.round(stop.offset * 100)}%`} aria-pressed={index === current} onPointerDown={event => dragStop(index, event)}
        onKeyDown={event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); setStop(index, { offset: round(Math.min(1, Math.max(0, stop.offset + (event.key === 'ArrowRight' ? 0.01 : -0.01)))) }); } }} />)}
    </div>
    {fill.type === 'linear' && <Slider label={t('gradientAngle')} min={0} max={360} step={1} value={fill.angle} formatLabel={v => `${v}°`} onChange={(_, angle) => onChange({ ...fill, angle })} />}
    <div className="art-fill__row">
      <Text.label>{t('gradientStops')}</Text.label>
      <span className="art-fill__spacer" />
      <Button size="small" variant="text" color="overlay" preventLoading icon={<IconPlus />} disabled={stops.length >= MAX_STOPS} aria-label={t('gradientAddStop')} tooltip={t('gradientAddStop')} onClick={addInGap} />
    </div>
    <div className="art-fill__stops">
      {sorted(stops).map(stop => <div key={stop.index} className={`art-fill__stop ${stop.index === current ? 'is-active' : ''}`} onPointerDown={() => setActive(stop.index)}>
        <Percent label={t('gradientPosition')} value={stop.offset} onCommit={offset => setStop(stop.index, { offset })} />
        <button type="button" className="art-fill__stop-color" aria-label={`${t('gradientStopColor')}: ${stop.color}`} aria-pressed={stop.index === current} onClick={() => setActive(stop.index)}>
          <span className="art-fill__stop-swatch" style={{ '--swatch': stopColor(stop) }} />
          <Text.caption contrast={8}>{stop.color.slice(1).toUpperCase()}</Text.caption>
        </button>
        <Percent label={t('opacity')} value={stop.opacity} onCommit={opacity => setStop(stop.index, { opacity })} />
        <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconMinus />} disabled={stops.length <= 2} aria-label={t('gradientRemoveStop')} tooltip={t('gradientRemoveStop')} onClick={() => remove(stop.index)} />
      </div>)}
    </div>
    <SolidPicker key={current} color={stops[current].color} alpha={stops[current].opacity} showAlpha brand={brand} used={used}
      onChange={({ color, alpha }) => setStop(current, { color, opacity: alpha })} />
  </div>;
}
